import { useEffect, useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { AiReceptionistEventType } from '@weave/schema-gen-ts/dist/shared/phone/events/vair/v1/vair.pb';
import {
  AIOutcome,
  Direction as CallDirection,
  CallStatus,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { PhoneEventsOrg } from '@wetags/phone-events';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { AiEventBadge, ChecklistChipMenuField } from '@frontend/phone-ui';
import { AI_EVENT_CONFIG } from '@frontend/phone-ui-widgets';
import { theme } from '@frontend/theme';
import {
  ChecklistMenuField,
  Chip,
  ListFieldProps,
  ModalControlModalProps,
  setValue,
  TableFilters,
  Text,
  Truncated,
  useFormField,
} from '@frontend/design-system';
import { useActiveLocationsMap } from '../../hooks/use-active-location-map';
import { CallRecordsFiltersType } from '../../hooks/use-phone-config-state-store';
import { formatDate } from '../../utils';
import { CheckListSelector } from '../filter-selectors/check-list-selector';
import { DatesRangeSelector } from '../filter-selectors/dates-range-selector';
import { HandledBySelectorFilter } from '../filter-selectors/handled-by-selector-filter';
import { LocationsSelector } from '../filter-selectors/locations-checklist-selectors';
import { RadioFilterSelector } from '../filter-selectors/radio-filter-selector';
import { DEFAULT_NO_TIME_PERIOD, TimePeriodSelector } from '../filter-selectors/time-period-selector';
import { getAiOutcomeIcon } from './ui-utils';

interface FilterProps {
  hasVoiceAi: boolean;
  hasFilterChanged: (filter: CallRecordsFiltersType) => boolean;
  filters: CallRecordsFiltersType;
  isMulti: boolean;
  onChange: (args: CallRecordsFiltersType) => void;
  phoneNumberOptions: Record<string, { name: string; numbers: { value: string; label: string; name: string }[] }>;
}

type TableTrayFilterProps = {
  modalProps: ModalControlModalProps;
  resetFilters: () => void;
};

const determineSetRelationship = (
  setA: Set<any>,
  setB: Set<any>
): 'subset' | 'intersection' | 'disjoint' | 'neutral' => {
  // Check if both sets are equal sets
  if (setA.size === 0 || setB.size === 0 || (setA.isSubsetOf(setB) && setB.isSubsetOf(setA))) {
    return 'neutral';
  }

  if (setA.isSubsetOf(setB) || setB.isSubsetOf(setA)) {
    return 'subset';
  }

  if (setA.isDisjointFrom(setB)) {
    return 'disjoint';
  }

  if (setA.intersection(setB).size > 0) {
    return 'intersection';
  }

  return 'neutral';
};

const getBanner = (
  locationToNumberMap: Record<string, string>,
  selectedPhoneNumbers: string[],
  selectedLocationIds: string[]
) => {
  const phoneNumberLocationSet = new Set(selectedPhoneNumbers.map((number) => locationToNumberMap[number]));
  const locationSet = new Set(selectedLocationIds);
  const relationship = determineSetRelationship(phoneNumberLocationSet, locationSet);

  let banner = undefined;
  if (relationship === 'intersection' || relationship === 'subset') {
    banner = {
      status: 'warn',
      message: i18next.t('Results will be limited to the selected Locations and Office Numbers that match.', {
        ns: 'calls',
      }),
    } as const;
  } else if (relationship === 'disjoint') {
    banner = {
      status: 'error',
      message: i18next.t(
        'None of the selected Office Numbers belong to any of the selected Locations. No results will be available.',
        { ns: 'calls' }
      ),
    } as const;
  }

  return banner;
};

const MAX_ALLOWED_OFFICE_NUMBERS = 10;

export const RecentCallsTrayFilter = ({
  hasVoiceAi,
  hasFilterChanged,
  filters,
  modalProps,
  onChange,
  isMulti,
  phoneNumberOptions: _phoneNumberOptions,
  resetFilters,
}: FilterProps & TableTrayFilterProps) => {
  const { t } = useTranslation('calls', { keyPrefix: 'calls-filter' });

  const [isAirEventsUiEnabled] = PhoneEventsOrg.useWetag('fe-air-events-ui');

  const allLocationsIdToNameMap = useActiveLocationsMap();

  const [trayFilters, setTrayFilters] = useState<CallRecordsFiltersType>(filters);

  const handleUpdateTrayFilters = (newFilters: Partial<CallRecordsFiltersType>) => {
    setTrayFilters((prevFilters) => ({ ...prevFilters, ...newFilters }));
  };

  useEffect(() => {
    setTrayFilters({
      ...filters,
      locationIds: filters.locationIds,
      startDate: filters.startDate,
      endDate: filters.endDate,
    });
  }, [filters.locationIds, filters.startDate, filters.endDate, filters]);

  const handleApplyTableFilters = () => {
    onChange(trayFilters);
    modalProps?.onClose();
  };

  const handleFiltersReset = () => {
    resetFilters();
    modalProps?.onClose();
  };

  const callDirectionOptions = {
    [CallDirection.DIRECTION_INBOUND]: t('Inbound Calls'),
    [CallDirection.DIRECTION_OUTBOUND]: t('Outbound Calls'),
  };

  const callResult = {
    [CallStatus.CALL_STATUS_MISSED]: (
      <Chip isResponsive variant={'critical'}>
        {'Missed'}
      </Chip>
    ),
    [CallStatus.CALL_STATUS_ABANDONED]: (
      <Chip isResponsive variant={'warn'}>
        {'Abandoned'}
      </Chip>
    ),
    [CallStatus.CALL_STATUS_ANSWERED]: (
      <Chip isResponsive variant={'primary'}>
        {'Answered'}
      </Chip>
    ),
    [CallStatus.CALL_STATUS_FORWARDED]: (
      <Chip isResponsive variant={'eggplant'}>
        {'Forwarded'}
      </Chip>
    ),
  };

  const aiCallOutcomeOptions = {
    [AIOutcome.AI_OUTCOME_ROUTE_MISSED]: (
      <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', alignItems: 'center', gap: theme.spacing(1) }}>
        {getAiOutcomeIcon({ outcome: AIOutcome.AI_OUTCOME_ROUTE_MISSED })} {t('Needs attention')}
      </div>
    ),
    [AIOutcome.AI_OUTCOME_FATAL]: (
      <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', alignItems: 'center', gap: theme.spacing(1) }}>
        {getAiOutcomeIcon({ outcome: AIOutcome.AI_OUTCOME_FATAL })} {t('Unable to answer')}
      </div>
    ),
    [AIOutcome.AI_OUTCOME_INCOMPLETE]: (
      <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', alignItems: 'center', gap: theme.spacing(1) }}>
        {getAiOutcomeIcon({ outcome: AIOutcome.AI_OUTCOME_INCOMPLETE })} {t('Incomplete')}
      </div>
    ),
    [AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED]: (
      <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', alignItems: 'center', gap: theme.spacing(1) }}>
        {getAiOutcomeIcon({ outcome: AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED })} {t('Office assisted')}
      </div>
    ),
    [AIOutcome.AI_OUTCOME_TEXT_HANDOFF]: (
      <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', alignItems: 'center', gap: theme.spacing(1) }}>
        {getAiOutcomeIcon({ outcome: AIOutcome.AI_OUTCOME_TEXT_HANDOFF })} {t('Continued over text')}
      </div>
    ),
    [AIOutcome.AI_OUTCOME_END_CALL]: (
      <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', alignItems: 'center', gap: theme.spacing(1) }}>
        {getAiOutcomeIcon({ outcome: AIOutcome.AI_OUTCOME_END_CALL })} {t('Successfully assisted patient')}
      </div>
    ),
  };

  const checkListField = useFormField(
    {
      type: 'checklist',
      value: filters.officePhoneNumbers,
    },
    [filters.officePhoneNumbers]
  );

  const checkDisabled = (number: string) => {
    return trayFilters.officePhoneNumbers
      ? trayFilters.officePhoneNumbers.length === MAX_ALLOWED_OFFICE_NUMBERS &&
          !trayFilters.officePhoneNumbers.includes(number)
      : false;
  };

  const phoneNumberOptions = useMemo(
    () =>
      Object.entries(_phoneNumberOptions)
        // sort by location name
        .sort(([, a], [, b]) => a.name.localeCompare(b.name)),
    [_phoneNumberOptions]
  );
  const locationToNumberMap = useMemo(
    () =>
      phoneNumberOptions.reduce((acc, [locationId, options]) => {
        options.numbers.forEach((number) => (acc[number.value] = locationId));

        return acc;
      }, {} as Record<string, string>),
    [phoneNumberOptions]
  );

  const banner = useMemo(
    () => getBanner(locationToNumberMap, trayFilters.officePhoneNumbers ?? [], trayFilters.locationIds),
    [trayFilters.officePhoneNumbers, trayFilters.locationIds, locationToNumberMap]
  );

  const renderTableTrayFilters = () => (
    <TableFilters
      width='medium'
      {...modalProps}
      trackingId='call-history-table-filter'
      onApplyClick={handleApplyTableFilters}
      onClearAllClick={handleFiltersReset}
      disableApplyButton={!hasFilterChanged(trayFilters) || banner?.status === 'error'}
      banner={banner}
    >
      {isMulti && (
        <TableFilters.Section sectionHeaderLabel={t('Filter')}>
          <div css={styles.traySection}>
            <LocationsSelector
              allLocationsOption={allLocationsIdToNameMap}
              onChange={(selLocationIds) => {
                handleUpdateTrayFilters({ locationIds: selLocationIds });
              }}
              selectedLocationsValue={trayFilters.locationIds}
              trackingId='recent-calls-location-selector-table-filter'
            />
          </div>
        </TableFilters.Section>
      )}

      <TableFilters.Section
        onClearClick={() => {
          handleUpdateTrayFilters({ officePhoneNumbers: [] });
          checkListField.onChange({ name: 'office-number', value: [] });
        }}
        sectionHeaderLabel={t('Office Numbers')}
      >
        <div css={styles.traySection}>
          <PhoneNumberSelection
            checkDisabled={checkDisabled}
            checkListField={checkListField}
            filteredPhoneNumberOptions={phoneNumberOptions}
            handleUpdateTrayFilters={handleUpdateTrayFilters}
            trackingId='recent-calls-office-number-selector-table-filter'
          />
        </div>
      </TableFilters.Section>

      <TableFilters.Section
        onClearClick={() =>
          handleUpdateTrayFilters({
            aiReceptionistHandled: undefined,
            handledByUserIds: [],
            allOfficeUsers: undefined,
          })
        }
        sectionHeaderLabel={t('Office User')}
      >
        <div css={styles.traySection}>
          <HandledBySelectorFilter
            filters={{
              aiReceptionistHandled: trayFilters.aiReceptionistHandled,
              handledByUserIds: trayFilters.handledByUserIds,
              allOfficeUsers: trayFilters.allOfficeUsers,
            }}
            setFilters={handleUpdateTrayFilters}
            showApplyButton={false}
            canShowCallout={false}
            trackingId='recent-calls-handled-by-selector-table-filter'
          />
        </div>
      </TableFilters.Section>

      <TableFilters.Section sectionHeaderLabel={t('Time Period')}>
        <div css={styles.traySection}>
          <TimePeriodSelector
            defaultPeriod={DEFAULT_NO_TIME_PERIOD}
            onChange={({ endDate, startDate }) => {
              handleUpdateTrayFilters({ startDate, endDate });
            }}
            startDate={trayFilters.startDate}
            endDate={trayFilters.endDate}
            trackingId='recent-calls-time-selector-table-filter'
          />
          <DatesRangeSelector
            onChange={({ endDate, startDate }) => {
              handleUpdateTrayFilters({ startDate: formatDate(startDate), endDate: formatDate(endDate) });
            }}
            startDate={trayFilters.startDate}
            endDate={trayFilters.endDate}
          />
        </div>
      </TableFilters.Section>

      {hasVoiceAi && (
        <TableFilters.Section
          onClearClick={() => handleUpdateTrayFilters(isAirEventsUiEnabled ? { aiEvents: [] } : { aiOutcomes: [] })}
          sectionHeaderLabel={
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                alignItems: 'center',
                gap: theme.spacing(0.5),
              }}
            >
              <Icon name='aiweave-assistant' size={16} />
              <Text as='span' weight='semibold'>
                {isAirEventsUiEnabled ? t('AI Receptionist Outcomes') : t('AI Call Outcome')}
              </Text>
            </div>
          }
        >
          <div css={styles.traySection}>
            {isAirEventsUiEnabled ? (
              <AiEventSelection
                selectedEvents={trayFilters.aiEvents ?? []}
                handleUpdateTrayFilters={handleUpdateTrayFilters}
              />
            ) : (
              <CheckListSelector<AIOutcome>
                label={t('AI Call Outcome')}
                onChange={(result) => {
                  handleUpdateTrayFilters({ aiOutcomes: result });
                }}
                options={aiCallOutcomeOptions}
                showLabel={false}
                value={trayFilters.aiOutcomes}
                trackingIdBase='recent-calls-ai-outcome-selector-table-filter'
              />
            )}
          </div>
        </TableFilters.Section>
      )}

      <TableFilters.Section
        onClearClick={() => handleUpdateTrayFilters({ status: [] })}
        sectionHeaderLabel={t('Results')}
      >
        <div css={styles.traySection}>
          <CheckListSelector<CallStatus>
            label={t('Results')}
            onChange={(result) => {
              handleUpdateTrayFilters({ status: result });
            }}
            options={callResult}
            showLabel={false}
            value={trayFilters.status}
            trackingIdBase='recent-calls-result-selector-table-filter'
          />
        </div>
      </TableFilters.Section>

      <TableFilters.Section
        onClearClick={() => handleUpdateTrayFilters({ callDirection: CallDirection.DIRECTION_UNKNOWN })}
        sectionHeaderLabel={t('Call Direction')}
      >
        <div css={styles.traySection}>
          <RadioFilterSelector<CallDirection>
            label={t('Call Direction')}
            onChange={(direction) => {
              handleUpdateTrayFilters({ callDirection: direction });
            }}
            options={callDirectionOptions}
            showLabel={false}
            value={trayFilters.callDirection}
            withBorder={false}
            trackingId='recent-calls-call-direction-selector-table-filter'
          />
        </div>
      </TableFilters.Section>
    </TableFilters>
  );

  return renderTableTrayFilters();
};

const callEvents = [
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_MISSED,
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_ANSWERED,
] as const;

const scheduleEvents = [
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_CANCELLED,
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_ADDED,
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_RESCHEDULED,
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_CONFIRMED,
] as const;

const fatalEvents = [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_FATAL] as const;

const renderAiEventOption = (eventType: AiReceptionistEventType, hasSeparator = false) => {
  const config = AI_EVENT_CONFIG[eventType];
  if (!config) {
    return null;
  }

  return (
    <ChecklistMenuField.Option
      key={eventType}
      textValue={config.text}
      value={String(eventType)}
      css={hasSeparator ? styles.aiEventOptionSeparator : undefined}
    >
      <AiEventBadge config={config} size='large' />
    </ChecklistMenuField.Option>
  );
};

const AiEventSelection = ({
  selectedEvents,
  handleUpdateTrayFilters,
}: {
  selectedEvents: AiReceptionistEventType[];
  handleUpdateTrayFilters: (newFilters: Partial<CallRecordsFiltersType>) => void;
}) => {
  const { t } = useTranslation('calls', { keyPrefix: 'calls-filter' });
  const eventFieldProps = useFormField(
    {
      type: 'checklist',
      value: selectedEvents,
    },
    [selectedEvents]
  );

  return (
    <ChecklistChipMenuField
      fieldProps={eventFieldProps}
      label={t('Select AI Receptionist Outcomes')}
      name='ai-events'
      onChange={(nextValue) => {
        handleUpdateTrayFilters({ aiEvents: nextValue as AiReceptionistEventType[] });
      }}
      trackingId='recent-calls-ai-events-selector-table-filter'
      showSearchBar={false}
      showSelectionBar={false}
    >
      {callEvents.map((eventType) => renderAiEventOption(eventType))}
      {scheduleEvents.map((eventType, index) => renderAiEventOption(eventType, index === 0))}
      {fatalEvents.map((eventType, index) => renderAiEventOption(eventType, index === 0))}
    </ChecklistChipMenuField>
  );
};

const PhoneNumberSelection = ({
  checkListField,
  handleUpdateTrayFilters,
  filteredPhoneNumberOptions,
  checkDisabled,
  trackingId,
}: {
  checkListField: ListFieldProps;
  handleUpdateTrayFilters: (newFilters: Partial<CallRecordsFiltersType>) => void;
  filteredPhoneNumberOptions: [string, { name: string; numbers: { value: string; label: string; name: string }[] }][];
  checkDisabled: (number: string) => boolean;
  trackingId?: string;
}) => {
  const { t } = useTranslation('calls', { keyPrefix: 'calls-filter' });
  const disabledMessage = t('You have reached the max number of selections');

  return (
    <ChecklistMenuField
      customRender={(props) => (
        <ChecklistMenuField.ChipInput
          {...props}
          onRemoveClick={(id) => {
            checkListField.onChange({ name: 'office-number', value: [id] });
            const nextState = setValue({ ...checkListField, type: 'checklist' }, id);
            handleUpdateTrayFilters({ officePhoneNumbers: [...nextState.value] });
          }}
        />
      )}
      {...checkListField}
      trackingId={trackingId}
      onChange={(e) => {
        checkListField.onChange(e);
        // @ts-expect-error - field types need to be fixed
        const nextState = setValue({ ...checkListField, type: 'checklist' }, e.value);
        handleUpdateTrayFilters({ officePhoneNumbers: [...nextState.value] });
      }}
      showSelectionBar={false}
      label={t('Select Office Numbers')}
      name='office-number'
      customSearchComparator={(value, searchTerm) => {
        // strip the text of anything but numbers, and compare both
        const normalizedValue = value.replace(/\D/g, '');
        const normalizedSearchTerm = searchTerm.replace(/\D/g, '');

        return normalizedValue.includes(normalizedSearchTerm);
      }}
    >
      {filteredPhoneNumberOptions.length === 1
        ? filteredPhoneNumberOptions[0][1].numbers.map((option) => (
            <ChecklistMenuField.Option
              key={option.value}
              disabled={checkDisabled(option.value)}
              disabledMessage={disabledMessage}
              value={option.value}
              css={{
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'grid', gridTemplateRows: 'auto auto' }}>
                <Truncated as={Text}>{option.name}</Truncated>
                {'\t'}
                <Text color='light'>{option.label}</Text>
              </div>
            </ChecklistMenuField.Option>
          ))
        : filteredPhoneNumberOptions.map(([key, options]) => (
            <ChecklistMenuField.Group key={key} label={options.name}>
              {options.numbers.map((option) => (
                <ChecklistMenuField.Option
                  key={option.value}
                  disabled={checkDisabled(option.value)}
                  disabledMessage={disabledMessage}
                  value={option.value}
                  css={{
                    alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateRows: 'auto auto' }}>
                    <Truncated as={Text}>{option.name}</Truncated>
                    {'\t'}
                    <Text color='light'>{option.label}</Text>
                  </div>
                </ChecklistMenuField.Option>
              ))}
            </ChecklistMenuField.Group>
          ))}
    </ChecklistMenuField>
  );
};

const styles = {
  heading: css`
    > label {
      font-weight: 700;
      font-size: ${theme.fontSize(20)};
    }
  `,

  traySection: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,

  aiEventOptionSeparator: css`
    border-top: 1px solid ${theme.colors.neutral10};
    margin-top: ${theme.spacing(1)};
    padding-top: ${theme.spacing(1)};
  `,
};
